import React, { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import HeaderNavTrigger from "./HeaderNavTrigger";
import HeaderPanelModal from "./HeaderPanelModal";
import {
  getStoredQuickOpenEntityType,
  openQuickOpenRecord,
  QUICK_OPEN_ENTITY_ORDER,
  QUICK_OPEN_ENTITY_TYPES,
  resolveQuickOpenTarget,
  storeQuickOpenEntityType,
  tryParseQuickOpenFromUrlOrPath,
} from "../../utilis/quickOpenRoutes";

const btnSecondary =
  "inline-flex items-center justify-center rounded-xl bg-slate-200 px-4 py-2.5 text-sm font-medium text-slate-800 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400";

const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50";

export default function HeaderQuickOpen({ icon }) {
  const [open, setOpen] = useState(false);
  const [entityType, setEntityType] = useState(getStoredQuickOpenEntityType);
  const [recordId, setRecordId] = useState("");
  const [error, setError] = useState("");
  const panelRef = useRef(null);
  const idInputRef = useRef(null);

  const canOpen = Boolean(resolveQuickOpenTarget(entityType, recordId));

  const handleClose = useCallback(() => {
    setOpen(false);
    setError("");
  }, []);

  const handleToggle = () => {
    if (!open) {
      setEntityType(getStoredQuickOpenEntityType());
      setRecordId("");
      setError("");
      setOpen(true);
    } else {
      handleClose();
    }
  };

  const handleEntityChange = (nextKey) => {
    setEntityType(nextKey);
    storeQuickOpenEntityType(nextKey);
    setError("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const result = openQuickOpenRecord(entityType, recordId, { newTab: true });
    if (!result.ok) {
      setError(result.error);
      return;
    }
    storeQuickOpenEntityType(result.entityKey);
    handleClose();
  };

  useEffect(() => {
    if (!open) return undefined;
    const focusTimer = window.setTimeout(() => {
      idInputRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(focusTimer);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, handleClose]);

  return (
    <>
      <HeaderNavTrigger label="Go to record" onClick={handleToggle}>
        {icon}
      </HeaderNavTrigger>

      <HeaderPanelModal
        open={open}
        onClose={handleClose}
        compact
        headerTone="light"
        title="Go to record"
        panelRef={panelRef}
        ariaLabelledBy="header-quick-open-title"
        footer={
          <div className="flex shrink-0 items-center justify-end gap-2.5 border-t border-slate-200 bg-white px-5 py-3.5">
            <button type="button" className={btnSecondary} onClick={handleClose}>
              Cancel
            </button>
            <button
              type="submit"
              form="header-quick-open-form"
              className={btnPrimary}
              disabled={!canOpen}
            >
              Open
              <ExternalLink size={15} strokeWidth={2.5} aria-hidden />
            </button>
          </div>
        }
      >
        <form
          id="header-quick-open-form"
          className="flex flex-col gap-5 bg-white px-5 py-5"
          onSubmit={handleSubmit}
        >
          <div>
            <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Record type
            </p>
            <div
              className="grid grid-cols-3 gap-1.5 rounded-xl border border-blue-100 bg-[#e8f0fe] p-1.5"
              role="radiogroup"
              aria-label="Record type"
            >
              {QUICK_OPEN_ENTITY_ORDER.map((key) => {
                const item = QUICK_OPEN_ENTITY_TYPES[key];
                const selected = entityType === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => handleEntityChange(key)}
                    className={`rounded-lg px-2 py-2.5 text-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                      selected
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-slate-600 hover:bg-blue-100 hover:text-blue-900"
                    }`}
                  >
                    <span className="block text-xs font-semibold leading-tight">
                      {item.shortLabel}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mb-0 mt-2 text-xs text-slate-500">
              {QUICK_OPEN_ENTITY_TYPES[entityType]?.description}
            </p>
          </div>

          <div>
            <label
              className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500"
              htmlFor="header-quick-open-id"
            >
              Record ID
            </label>
            <input
              ref={idInputRef}
              id="header-quick-open-id"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-inner placeholder:text-slate-400 transition focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
              placeholder={
                entityType === "client"
                  ? "10953"
                  : entityType === "education"
                    ? "3585"
                    : "1687"
              }
              value={recordId}
              onChange={(event) => {
                const raw = event.target.value;
                setError("");
                const fromLink = tryParseQuickOpenFromUrlOrPath(raw);
                if (fromLink) {
                  setRecordId(fromLink.id);
                  if (fromLink.entityKey !== entityType) {
                    setEntityType(fromLink.entityKey);
                    storeQuickOpenEntityType(fromLink.entityKey);
                  }
                  return;
                }
                setRecordId(raw.replace(/\D/g, ""));
              }}
            />
            <p className="mb-0 mt-2 text-xs leading-relaxed text-slate-500">
              Numbers only. You can also paste a record link to fill the ID. Press{" "}
              <kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5 font-sans text-[10px] font-medium text-slate-600 shadow-sm">
                Enter
              </kbd>{" "}
              to open in a new tab.
            </p>
          </div>

          {error ? (
            <div
              className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          ) : null}
        </form>
      </HeaderPanelModal>
    </>
  );
}
